// One game on the scoreboard (B-385): the two teams stacked, each a line of
// its own with the score at its end, the way a sports scoreboard sets a game.
// Stacked rather than side by side because a grid of games puts several
// across a screen, and side by side a name had half a tile: "The
// Accountant's …" was every other name at three across 1280 (UX-11). Stacked,
// a name has the tile's width, and the grid takes any number of games at any
// width.

import type { AwardCardView, MatchupSummary, StatsFreshnessShape } from '@fsp/domain';
import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeagueBase } from '../../data/hooks';
import { morph } from '../../shell/morph';
import { Detail } from '../../data/detail';
import { LiveDot, ScoreNumber, TeamMark } from '../../kit';
import { Colored, Cover, SplitBar } from '../../kit/ui';
import { Term } from '../../kit/Why';
import { AwardIcon } from '../shared/AwardKit';
import { TeamName } from '../shared/Names';
import { num } from '../shared/notComputed';

type Side = MatchupSummary['home'];

export function GameTile({
  m,
  awards,
  stats,
  big = false,
  top: topTeam,
}: {
  m: MatchupSummary;
  /** The week's awards: a final wears each side's first one (AWARDS.md). */
  awards?: AwardCardView[];
  /** How fresh the week's lines are (BACKLOG L2): "Live" only while they are. */
  stats: StatsFreshnessShape;
  /** Bigger type, on a large screen's scoreboard. */
  big?: boolean;
  /** The team that reads on top, when it is not mine: the team whose season is on screen. */
  top?: string;
}) {
  const base = useLeagueBase();
  const navigate = useNavigate();
  const to = `${base}/matchup/${m.id}`;
  // THE CARD GROWS INTO ITS MATCHUP (B-409): the tile is the morph's hero and
  // lands on the matchup's score card, as a team's name grows into its page.
  const follow = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    const hero = e.currentTarget.parentElement;
    morph(() => navigate(to), { motion: 'deeper', hero });
  };
  // MY TEAM ON TOP, as it reads on the left of a matchup card, or the team
  // whose season is on screen; otherwise the home side, the card's left.
  const flip = topTeam === undefined ? m.away.team.isMine : m.away.team.id === topTeam;
  const top = flip ? m.away : m.home;
  const bottom = flip ? m.home : m.away;
  const p = m.winProbabilityHome;
  const topChance = p === null ? null : flip ? 1 - p : p;
  // Whole percents that add to 100: 51.5 and 48.5 rounded were 52 and 49.
  const topPct = topChance === null ? 0 : Math.round(topChance * 100);
  const mine = m.home.team.isMine || m.away.team.isMine;
  const chipFor = (teamId: string) =>
    m.status !== 'final'
      ? undefined
      : (awards ?? []).find(
          (a) =>
            !a.asItStands && a.period === m.period && a.winners.some((w) => w.team.id === teamId),
        );
  return (
    <div
      className={`fsp-gt fsp-cover-host${big ? ' fsp-gt-big' : ''}`}
      data-mine={mine ? '' : undefined}
    >
      <Cover to={to} label={coverLabel(m)} onFollow={follow} />
      <Team m={m} s={top} other={bottom} chip={chipFor(top.team.id)} big={big} />
      <Team m={m} s={bottom} other={top} chip={chipFor(bottom.team.id)} big={big} />
      <div className="fsp-gt-foot">
        <span className="fsp-gt-status">
          <Status m={m} stats={stats} />
        </span>
        {/* EACH SIDE'S CHANCE, at Everything: win probability is a Pro number
            (DETAIL_LEVELS §Scoreboard). The top team's at the bar's left end,
            in its color, the bottom team's at the right, as the matchup card
            set each under its side; named by an abbreviation, two teams can
            share one (*THE*). A final's is its result, which the bold line
            already says. */}
        {m.status === 'final' || topChance === null ? null : (
          <Detail min="pro">
            <span
              className="fsp-gt-chance"
              role="img"
              aria-label={`Chance to win: ${top.team.name} ${topPct}%, ${bottom.team.name} ${100 - topPct}%`}
            >
              <span className="fsp-num" aria-hidden>
                {topPct}%
              </span>
              <SplitBar
                share={topChance}
                left={top.team.colors.primary}
                right={bottom.team.colors.primary}
              />
              <span className="fsp-num" aria-hidden>
                {100 - topPct}%
              </span>
            </span>
          </Detail>
        )}
      </div>
    </div>
  );
}

function Team({
  m,
  s,
  other,
  chip,
  big,
}: {
  m: MatchupSummary;
  s: Side;
  other: Side;
  chip: AwardCardView | undefined;
  big: boolean;
}) {
  // Ahead, or won: a final is marked by who won it, which on a tie the league
  // broke is not the higher score (N1, B-232).
  const lead = m.status === 'final' ? m.winnerTeamId === s.team.id : s.score > other.score;
  const lost = m.status === 'final' && m.winnerTeamId !== null && !lead;
  const r = s.team.record;
  return (
    <Colored
      className="fsp-gt-team"
      colors={{ '--team': s.team.colors.primary }}
      data-tone={s.team.isMine ? 'mine' : undefined}
      data-lead={lead ? 'true' : undefined}
      data-lost={lost ? 'true' : undefined}
    >
      <span className="fsp-gt-mark">
        <TeamMark team={s.team} size={big ? 34 : 26} />
      </span>
      <span className="fsp-gt-name">
        <TeamName team={s.team} />
      </span>
      {/* The kit's score, so a change glows as it does on every other card. */}
      <span className="fsp-gt-score">
        <ScoreNumber
          value={s.score}
          size={big ? 30 : 22}
          color={lead ? 'var(--fsp-ink-primary)' : 'var(--fsp-ink-secondary)'}
        />
      </span>
      <span className="fsp-gt-line">
        <span className="fsp-gt-who">
          {r.wins}–{r.losses}
          {r.ties ? `–${r.ties}` : ''} · {s.team.ownerName}
        </span>
        {chip !== undefined ? (
          // The award's line when asked (B-374), as the matchup card's chip.
          // Inline in a span: in the line's flex row the Term was a block,
          // and its padding made a line with a chip taller than one without.
          <span className="fsp-gt-chip">
            <Term tip={chip.line} name={chip.name} className="fsp-matchup-term">
              <span className="fsp-award-chip" data-tone={chip.tone}>
                <AwardIcon icon={chip.icon} size={11} />
                {chip.name}
              </span>
            </Term>
          </span>
        ) : m.status !== 'final' && (s.projected !== null || other.projected !== null) ? (
          // THE PROJECTED FINAL AND WHO IS STILL TO PLAY, from Standard
          // (DETAIL_LEVELS §Scoreboard). Not on a final, where it is the
          // score; not where nobody has worked one out.
          <Detail min="standard">
            <span className="fsp-gt-proj">
              {s.yetToPlay} to play · proj <span className="fsp-num">{num(s.projected)}</span>
            </span>
          </Detail>
        ) : null}
      </span>
    </Colored>
  );
}

function Status({ m, stats }: { m: MatchupSummary; stats: StatsFreshnessShape }) {
  if (m.status === 'final') {
    const margin = Math.abs(m.home.score - m.away.score);
    return (
      <>
        <span className="fsp-gt-final">Final</span>
        {/* The margin, a number, from Standard; none for a level score. */}
        {m.winnerTeamId !== null && margin > 0 ? (
          <Detail min="standard">
            <span className="fsp-muted">
              · by <span className="fsp-num">{margin.toFixed(1)}</span>
            </span>
          </Detail>
        ) : null}
      </>
    );
  }
  // A week under way whose numbers arrive overnight is not live (round 3, N5).
  if (m.status === 'live') {
    return stats.state === 'live' ? (
      <LiveDot label={m.liveLabel ? `Live · ${m.liveLabel}` : 'Live'} />
    ) : (
      <span className="fsp-gt-strong">In progress</span>
    );
  }
  return <span className="fsp-muted">Upcoming</span>;
}

/** What the tile's door says to a screen reader: whose game, and where it stands. */
function coverLabel(m: MatchupSummary): string {
  const where = m.status === 'final' ? 'final' : m.status === 'live' ? 'in progress' : 'upcoming';
  return `${m.home.team.name} ${m.home.score.toFixed(1)}, ${m.away.team.name} ${m.away.score.toFixed(1)}, ${where}: open the matchup`;
}
